import React, { Fragment, useState } from 'react';
import Box from 'components/atoms/Box/Box';
import Typography from 'components/atoms/Typography/Typography';
import Button from 'components/atoms/Button/Button';
import TextInput from 'components/atoms/TextInput/TextInput';
import GridContainer from 'components/atoms/GridContainer/GridContainer';
import GridItem from 'components/atoms/GridItem/GridItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import CheckboxGroup from 'components/molecules/CheckboxGroup/CheckboxGroup';
import { useRecoilState } from 'recoil';
import {
  fitnessPreferencesCheckboxGroupAtom,
  profileInfoAtom,
  nutritionPreferencesCheckboxGroupAtom,
} from 'dataLayer/recoil/pages/myProfile/atoms';
import Title from 'components/atoms/Title/Title';
import FormControl from 'components/atoms/FormControl/FormControl';
import { InputLabel, MenuItem, Select } from '@material-ui/core';
import {
  recommenderExercisesRequestIdAtom,
  recommenderRecipesRequestIdAtom,
  recommenderMealplanRequestIdAtom,
} from 'dataLayer/recoil/apiData/requestIdAtoms';
import {
  apiPutExercise,
  apiPutNutrition,
  apiPutUserProfile,
  apiDeleteGenotype,
  apiGetGenotype,
  apiPostGenotype,
} from 'dataLayer/api/apiClient';
import { genotypeStatusAtom } from 'dataLayer/recoil/apiData/atoms';
import Paragraph from 'components/atoms/Paragraph/Paragraph';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';

const { PUBLIC_URL } = process.env;

export default function Onboarding() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [profileInfo, setProfileInfo] = useRecoilState(profileInfoAtom);
  const [genotypeStatus, setGenotypeStatus] = useRecoilState(
    genotypeStatusAtom
  );
  const [fitnessPreferences, setFitnessPreferences] = useRecoilState(
    fitnessPreferencesCheckboxGroupAtom
  );
  const [nutritionPreferences, setNutritionPreferences] = useRecoilState(
    nutritionPreferencesCheckboxGroupAtom
  );
  const [
    recommenderRecipesRequestId,
    setRecommenderRecipesRequestId,
  ] = useRecoilState(recommenderRecipesRequestIdAtom);
  const [
    recommenderMealplanRequestId,
    setRecommenderMealplanRequestId,
  ] = useRecoilState(recommenderMealplanRequestIdAtom);
  const [
    recommenderExercisesRequestId,
    setRecommenderExercisesRequestId,
  ] = useRecoilState(recommenderExercisesRequestIdAtom);

  const handleProfileInfoChange = (e) => {
    const { name, value } = e.target;

    let newProfileInfo;
    if (
      name === 'year_of_birth' ||
      name === 'gender' ||
      name === 'height' ||
      name === 'weight'
    ) {
      //only year of birth and gender are saved in their fields on API
      newProfileInfo = { ...profileInfo, [name]: value };
    } else {
      //rest of the fields go to extra_data field
      newProfileInfo = {
        ...profileInfo,
        extra_data: { ...profileInfo.extra_data, [name]: value },
      };
    }
    setProfileInfo(newProfileInfo);
  };

  const handleProfileInfoSave = () => {
    apiPutUserProfile(profileInfo).then(() => setCurrentStep(currentStep + 1));
  };

  const handleFitnessPreferencesSave = () => {
    setRecommenderExercisesRequestId(recommenderExercisesRequestId + 1);
    let requestData = {};
    if (fitnessPreferences) {
      fitnessPreferences.forEach((element) => {
        requestData[element.name] = element.checked;
      });
      apiPutExercise(requestData).then(() => setCurrentStep(currentStep + 1));
    }
  };

  const handleNutritionPreferencesSave = () => {
    setRecommenderRecipesRequestId(recommenderRecipesRequestId + 1);
    //setRecommenderMealplanRequestId(recommenderMealplanRequestId + 1);
    let requestData = {};
    if (nutritionPreferences) {
      nutritionPreferences.forEach((element) => {
        requestData[element.name] = element.checked;
      });
      apiPutNutrition(requestData).then(() => setCurrentStep(currentStep + 1));
    }
  };

  const getUploadUrlAndUploadGenotype = (params, file) => {
    apiPostGenotype(params).then(() => {
      apiGetGenotype().then((resp) => {
        const uploadUrl = resp.source_details.upload_url;

        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);

        xhr.upload.onprogress = (ev) => {
          setUploadProgress((ev.loaded * 100) / ev.total);
        };

        xhr.onload = () => {
          const status = xhr.status;
          if (status === 200) {
            setUploadProgress(100);
            setGenotypeStatus(resp);
          }
        };

        xhr.onerror = (err) => {
          console.log(err);
          setUploadProgress(100);
        };
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    });
  };

  const handleGenotypeSave = () => {
    window.location.href = `${window.location.origin}${PUBLIC_URL}`;
  };

  const handleUploadFileOnChange = async ({ target }) => {
    const file = target.files[0];
    const params = {
      fileupload: {
        content_type: file.type,
        method: 'PUT',
        original_filename: file.name,
      },
    };

    try {
      //genotype needs to be deleted to make upload
      await apiDeleteGenotype();
      getUploadUrlAndUploadGenotype(params, file);
    } catch (error) {
      //API returns 404 when user doesn't have genotype uploaded
      console.log(error.response.data);
      //when there is no genotype data already uploaded or it is deleted previously
      getUploadUrlAndUploadGenotype(params, file);
    }
  };

  const isGenotypeUploaded = genotypeStatus.status === 404 ? false : true;

  return (
    <Fragment>
      {currentStep === 0 && (
        <Box>
          <Box mb={2}>
            <Typography variant="h1">
              <Trans i18nKey="dna24.profile.profileInfo" />
            </Typography>
            <Box display="flex" mt={2}>
              <Box mr={2}>
                <TextInput
                  label={t('dna24.profile.firstName')}
                  name="first_name"
                  value={profileInfo.extra_data.first_name}
                  onChange={handleProfileInfoChange}
                />
              </Box>
              <Box mr={2}>
                {/* TODO: below code should be our select component and documented in storybook */}
                <FormControl variant="filled">
                  <InputLabel>
                    <Trans i18nKey="dna24.profile.gender" />
                  </InputLabel>
                  <Select
                    name="gender"
                    value={profileInfo.gender}
                    onChange={handleProfileInfoChange}
                  >
                    <MenuItem value="male">
                      <Trans i18nKey="dna24.profile.male" />
                    </MenuItem>
                    <MenuItem value="female">
                      <Trans i18nKey="dna24.profile.female" />
                    </MenuItem>
                    <MenuItem value="other">
                      <Trans i18nKey="dna24.profile.other" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box display="flex" my={2}>
              <Box mr={2}>
                <TextInput
                  label={t('dna24.profile.yearOfBirth')}
                  name="year_of_birth"
                  type="number"
                  value={profileInfo.year_of_birth}
                  onChange={handleProfileInfoChange}
                />
              </Box>

              <Box mr={2}>
                <TextInput
                  label={t('dna24.profile.ethnicity')}
                  name="ethnicity"
                  value={profileInfo.extra_data.ethnicity}
                  onChange={handleProfileInfoChange}
                />
              </Box>
            </Box>
            <Box m={1}>
              <GridContainer spacing={2}>
                <GridItem>
                  <Button
                    variant="contained"
                    onClick={handleProfileInfoSave}
                    disabled={
                      profileInfo.gender == null ||
                      profileInfo.year_of_birth == null ||
                      profileInfo.extra_data.ethnicity == null
                    }
                  >
                    <Trans i18nKey="dna24.profile.save" />
                  </Button>
                </GridItem>
              </GridContainer>
            </Box>
          </Box>
        </Box>
      )}

      {currentStep === 1 && (
        <Box mt={3}>
          <Box mt={2}>
            <Typography variant="h1">
              <Trans i18nKey="dna24.profile.wellnessInfo" />
            </Typography>
            <Box p={1}>
              <Typography variant="h2">
                <Trans i18nKey="dna24.profile.basicWellnessInfo" />
              </Typography>
              <Box mt={1} display="flex">
                <Box mr={2}>
                  <TextInput
                    label={t('dna24.profile.height')}
                    name="height"
                    value={profileInfo.height}
                    onChange={handleProfileInfoChange}
                    type="number"
                    InputProps={{
                      endAdornment: (
                        <Box ml={1}>
                          <InputAdornment position="start">
                            <Typography>cm</Typography>
                          </InputAdornment>
                        </Box>
                      ),
                    }}
                  ></TextInput>
                </Box>

                {/* <Box mr={1}>
                <TextInput
                  label="height"
                  name="heightSecond"
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <Box ml={1}>
                        <InputAdornment position="start">
                          <Typography>''</Typography>
                        </InputAdornment>
                      </Box>
                    ),
                  }}
                ></TextInput>
              </Box> */}

                <Box mr={2}>
                  <TextInput
                    label={t('dna24.profile.weight')}
                    name="weight"
                    value={profileInfo.weight}
                    onChange={handleProfileInfoChange}
                    type="number"
                    InputProps={{
                      endAdornment: (
                        <Box ml={1}>
                          <InputAdornment position="start">
                            <Typography>kg</Typography>
                          </InputAdornment>
                        </Box>
                      ),
                    }}
                  ></TextInput>
                </Box>
              </Box>
            </Box>
            <Box m={1}>
              <GridContainer spacing={2}>
                <GridItem>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setCurrentStep(currentStep - 1);
                    }}
                  >
                    <Trans i18nKey="dna24.profile.back" />
                  </Button>
                </GridItem>
                <GridItem>
                  <Button
                    variant="contained"
                    onClick={handleProfileInfoSave}
                    disabled={
                      profileInfo.weight <= 0 || profileInfo.height <= 0
                    }
                  >
                    <Trans i18nKey="dna24.profile.save" />
                  </Button>
                </GridItem>
              </GridContainer>
            </Box>
          </Box>
        </Box>
      )}

      {currentStep === 2 && (
        <Box mt={3}>
          <Box mt={2}>
            <Box p={1}>
              <Title variant="h2">
                <Trans i18nKey="dna24.profile.fitnessPreferences" />
              </Title>
              <Box mt={1} display="flex">
                <CheckboxGroup
                  items={fitnessPreferences}
                  formLabel="Exercise Type Preference"
                  onSelectionChange={(newData) =>
                    setFitnessPreferences(newData)
                  }
                />
              </Box>
            </Box>

            <Box m={1}>
              <GridContainer spacing={2}>
                <GridItem>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setCurrentStep(currentStep - 1);
                    }}
                  >
                    <Trans i18nKey="dna24.profile.back" />
                  </Button>
                </GridItem>
                <GridItem>
                  <Button
                    variant="contained"
                    onClick={handleFitnessPreferencesSave}
                  >
                    <Trans i18nKey="dna24.profile.save" />
                  </Button>
                </GridItem>
              </GridContainer>
            </Box>
          </Box>
        </Box>
      )}

      {currentStep === 3 && (
        <Box mt={3} mb={3}>
          <Box mt={2}>
            <Box p={1}>
              <Title variant="h2">
                <Trans i18nKey="dna24.profile.nutritionPreferences" />
              </Title>
              <Box mt={1} display="flex">
                <CheckboxGroup
                  formLabel={t('dna24.profile.foodTypePreference')}
                  items={nutritionPreferences}
                  onSelectionChange={(newData) =>
                    setNutritionPreferences(newData)
                  }
                />
              </Box>
            </Box>

            <Box m={1}>
              <GridContainer spacing={2}>
                <GridItem>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setCurrentStep(currentStep - 1);
                    }}
                  >
                    <Trans i18nKey="dna24.profile.back" />
                  </Button>
                </GridItem>
                <GridItem>
                  <Button
                    variant="contained"
                    onClick={handleNutritionPreferencesSave}
                  >
                    <Trans i18nKey="dna24.profile.save" />
                  </Button>
                </GridItem>
              </GridContainer>
            </Box>
          </Box>
        </Box>
      )}

      {currentStep === 4 && (
        <Box mt={3}>
          <Box mt={2}>
            <Typography variant="h1">GENOTYPE DATA</Typography>
            <Box mt={2}>
              <Box mb={2}>
                {isGenotypeUploaded ? (
                  <Box>
                    <Paragraph>
                      <Trans i18nKey="dna24.profile.genotypeAlreadyExists" />
                    </Paragraph>
                    <Paragraph>
                      <Trans i18nKey="dna24.profile.genotypeFile" />{' '}
                      {genotypeStatus &&
                        genotypeStatus.source_details &&
                        genotypeStatus.source_details.original_filename}
                    </Paragraph>
                  </Box>
                ) : (
                  <Paragraph>
                    <Trans i18nKey="dna24.profile.genotypeDoesNotExists" />
                  </Paragraph>
                )}
              </Box>
              <Box>
                <Paragraph>
                  <Trans i18nKey="dna24.profile.genotypeUploadInstructions" />
                </Paragraph>
                <input type="file" onChange={handleUploadFileOnChange} />
                <Box>
                  <Paragraph>
                    {t('dna24.profile.genotypeUploadProgress', {
                      uploadProgress: uploadProgress,
                    })}
                  </Paragraph>
                </Box>
              </Box>
            </Box>

            <Box m={1}>
              <GridContainer spacing={2}>
                <GridItem>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setCurrentStep(currentStep - 1);
                    }}
                  >
                    <Trans i18nKey="dna24.profile.back" />
                  </Button>
                </GridItem>
                <GridItem>
                  <Button
                    variant="contained"
                    onClick={handleGenotypeSave}
                    disabled={
                      uploadProgress !== 100 && isGenotypeUploaded !== true
                    }
                  >
                    <Trans i18nKey="dna24.profile.finish" />
                  </Button>
                </GridItem>
              </GridContainer>
            </Box>
          </Box>
        </Box>
      )}
    </Fragment>
  );
}
